import { ref } from 'vue';
import { useRegisterSW } from 'virtual:pwa-register/vue';

/**
 * Composable autour de virtual:pwa-register/vue. Expose les états
 * needRefresh / offlineReady + une action applyUpdate qui force le
 * reload de l'app sur le nouveau service worker.
 *
 * Vérifie périodiquement (1 h) auprès du SW si une nouvelle version
 * est disponible — utile pour les sessions longues.
 */

const CHECK_INTERVAL = 60 * 60 * 1000; // 1 h

export function usePwaUpdate() {
  const updating = ref(false);
  const updateError = ref<string | null>(null);

  const {
    needRefresh,
    offlineReady,
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl: string, registration: ServiceWorkerRegistration | undefined) {
      if (registration) {
        setInterval(() => {
          void registration.update();
        }, CHECK_INTERVAL);
      }
    },
    onRegisterError(_error: unknown) {
      /* ignored — pas de SW en dev, ou environnement isolé */
    },
  });

  async function applyUpdate(): Promise<void> {
    updating.value = true;
    updateError.value = null;
    try {
      await updateServiceWorker(true);
      // Si la page ne s'est pas rechargée après 3 s, on force.
      setTimeout(() => {
        needRefresh.value = false;
        updating.value = false;
        window.location.reload();
      }, 3000);
    } catch {
      updateError.value = 'Erreur lors de la mise à jour';
      updating.value = false;
    }
  }

  function dismissUpdate(): void {
    needRefresh.value = false;
  }

  function dismissOfflineReady(): void {
    offlineReady.value = false;
  }

  return {
    needRefresh,
    offlineReady,
    updating,
    updateError,
    applyUpdate,
    dismissUpdate,
    dismissOfflineReady,
  };
}
